import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PostCard from '../components/PostCard';
import { config } from '../../config';

export default function UserPosts() {
  const { idUtilisateur } = useParams();
  const navigate = useNavigate();
  const [author, setAuthor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = user.id_utilisateur || user.id;
  const API_BASE = config.apiBase;

  const fetchAuthorPosts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = API_BASE + '/posts/get_user_posts.php'
        + '?id_utilisateur=' + encodeURIComponent(idUtilisateur);
      const response = await fetch(url, {
        headers: { Authorization: 'Bearer ' + token }
      });
      const data = await response.json();

      if (!data.success) {
        toast.error(data.message || 'Impossible de charger cette page');
        setPosts([]);
        return;
      }

      const normalizedAuthor = data.user ? {
        ...data.user,
        photo_profil: config.normalizeProfilePhoto(data.user.photo_profil)
      } : null;

      const normalizedPosts = (data.posts || []).map((post) => ({
        ...post,
        media_url: config.normalizePostMedia(post.media_url),
        photo_profil: config.normalizeProfilePhoto(post.photo_profil)
      }));

      setAuthor(normalizedAuthor);
      setPosts(normalizedPosts);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, idUtilisateur]);

  useEffect(() => {
    fetchAuthorPosts();
  }, [fetchAuthorPosts]);

  const handleLike = async (idPost) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_BASE + '/posts/like_post.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({ id_post: idPost })
      });
      const data = await response.json();
      if (data.success) {
        setPosts((previous) => previous.map((post) => (
          Number(post.id_post) === Number(idPost)
            ? { ...post, likes: data.likes, user_liked: data.liked }
            : post
        )));
      }
    } catch (error) {
      toast.error('Erreur lors du like');
    }
  };

  const handleDelete = async (idPost) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_BASE + '/posts/delete_post.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({ id_post: idPost })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Post supprim�');
        fetchAuthorPosts();
      } else {
        toast.error(data.message || 'Suppression impossible');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleShare = async (post) => {
    const shareUrl = window.location.origin
      + window.location.pathname
      + '#post-' + post.id_post;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Publication',
          text: post.contenu || 'Publication CROUS Niger INFO',
          url: shareUrl
        });
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Lien copi� dans le presse-papier');
    } catch (error) {
      toast.error('Impossible de copier le lien');
    }
  };

  const initials = `${author?.prenom?.[0] || ''}${author?.nom?.[0] || ''}`;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Retour
          </button>
          <div>
            <h1 className="text-lg font-bold text-blue-700 md:text-2xl">
              Page de publications
            </h1>
            <p className="text-xs text-gray-500 md:text-sm">
              Les posts publi�s par cet utilisateur
            </p>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-3xl">
        <section className="border-b border-gray-200 bg-white px-4 py-6">
          {loading && !author ? (
            <div className="h-24 animate-pulse rounded bg-gray-100" />
          ) : author ? (
            <div className="flex items-center gap-4">
              {author.photo_profil ? (
                <img
                  src={author.photo_profil}
                  alt={`${author.prenom} ${author.nom}`}
                  className="h-20 w-20 rounded-full border border-gray-200 object-cover"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blue-600 text-2xl font-bold text-white">
                  {initials || '?'}
                </div>
              )}
              <div className="min-w-0">
                <h2 className="truncate text-2xl font-bold text-gray-900">
                  {author.prenom} {author.nom}
                </h2>
                <p className="capitalize text-gray-500">
                  {author.role || 'Utilisateur'}
                </p>
                <p className="mt-1 text-sm font-medium text-green-700">
                  {posts.length} publication{posts.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Utilisateur introuvable.</p>
          )}
        </section>

        {loading && (
          <div className="flex justify-center py-10">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          </div>
        )}

        {!loading && posts.length === 0 && (
          <div className="m-4 border border-gray-200 bg-white p-8 text-center text-gray-500">
            Aucune publication trouv�e pour cet utilisateur.
          </div>
        )}

        {!loading && posts.map((post) => (
          <div id={`post-${post.id_post}`} key={post.id_post}>
            <PostCard
              post={post}
              currentUserId={currentUserId}
              onLike={handleLike}
              onDelete={handleDelete}
              onShare={handleShare}
            />
          </div>
        ))}
      </main>
    </div>
  );
}
